/**
 * 星云具身数字人 SDK 封装
 *
 * 基于魔珐星云具身驱动 JS SDK (xmovAvatar) 封装，
 * 提供数字人实例创建、初始化、说话、状态切换、销毁等能力。
 *
 * 接入文档：https://xingyun3d.com/developers/52-183
 *
 * 使用示例：
 *   var inst = await ttx.xingyun.create({
 *       containerId: '#xingyun-container',
 *       appId: 'your_appid',
 *       appSecret: 'your_appsecret'
 *   });
 *   ttx.xingyun.speak('欢迎使用');
 *   ttx.xingyun.destroy();
 *
 * 配置可通过 ttx.config.xingyun 全局预设：
 *   ttx.config.xingyun = { appId: 'xxx', appSecret: 'xxx' };
 *   之后 create() 可省略 appId/appSecret。
 */
(function () {
    var SDK_URL = 'https://media.xingyun3d.com/xingyun3d/general/litesdk/xmovAvatar@latest.js';
    var DEFAULT_GATEWAY = 'https://nebula-agent.xingyun3d.com/user/v1/ttsa/session';
    var READY_TIMEOUT = 20000; // 初始化就绪兜底超时

    var _instance = null;        // 当前数字人实例
    var _loadingPromise = null;  // 脚本加载 Promise（去重）

    /** 动态加载 xmovAvatar 脚本（去重） */
    function loadSdk() {
        if (window.XmovAvatar) return Promise.resolve(window.XmovAvatar);
        if (_loadingPromise) return _loadingPromise;
        _loadingPromise = new Promise(function (resolve, reject) {
            var script = document.createElement('script');
            script.src = SDK_URL;
            script.async = true;
            script.onload = function () {
                if (window.XmovAvatar) {
                    resolve(window.XmovAvatar);
                } else {
                    _loadingPromise = null;
                    reject(new Error('xmovAvatar 脚本加载完成但未挂载到 window'));
                }
            };
            script.onerror = function () {
                _loadingPromise = null;
                reject(new Error('xmovAvatar 脚本加载失败: ' + SDK_URL));
            };
            document.head.appendChild(script);
        });
        return _loadingPromise;
    }

    /** 从 ttx.config.xingyun 读取全局预设配置 */
    function _getGlobalConfig() {
        return (ttx.config && ttx.config.xingyun) || {};
    }

    ttx.xingyun = {
        /**
         * 加载 xmovAvatar 脚本
         * @returns {Promise<Function>} XmovAvatar 构造函数
         */
        loadSdk: loadSdk,

        /**
         * 初始化：预加载 xmovAvatar 脚本
         * 建议在页面入口（index.html）xkk.umd.js 加载后立即调用，
         * 提前加载星云 SDK，避免首次创建数字人时的脚本加载等待。
         * @returns {Promise<Function>} XmovAvatar 构造函数
         */
        init: function () {
            return loadSdk();
        },

        /**
         * 创建数字人实例并初始化连接
         *
         * @param {object} options
         *   containerId          - 容器元素选择器，如 '#xingyun-container'（必填）
         *   appId                - 数字人 appId（必填，可由 ttx.config.xingyun 预设）
         *   appSecret            - 数字人 appSecret（必填，可由 ttx.config.xingyun 预设）
         *   gatewayServer        - 服务接口地址，默认星云官方地址
         *   headers              - 自定义请求头
         *   hardwareAcceleration - 硬件加速：'default' | 'prefer-hardware' | 'prefer-software'
         *   initModel            - 初始化模式：'normal' | 'invisible'，默认 'normal'
         *   enableLogger         - 是否打印 sdk 日志，默认 false
         *   onStateChange        - 状态变化回调
         *   onStatusChange       - 状态变化回调
         *   onMessage            - 消息回调
         *   onNetworkInfo        - 网络状况回调
         *   onVoiceStateChange   - 音频播放状态回调
         *   onDownloadProgress   - 资源下载进度回调
         *   onWidgetEvent        - Widget 事件回调
         * @returns {Promise<object>} 数字人实例
         */
        create: function (options) {
            var opts = options || {};
            var cfg = _getGlobalConfig();

            var containerId = opts.containerId;
            var appId = opts.appId || cfg.appId;
            var appSecret = opts.appSecret || cfg.appSecret;

            if (!containerId) {
                return Promise.reject(new Error('[ttx.xingyun] containerId 不能为空'));
            }
            if (!appId || !appSecret) {
                return Promise.reject(new Error('[ttx.xingyun] appId / appSecret 不能为空，请配置 ttx.config.xingyun 或传入参数'));
            }

            // 若已有实例，先销毁
            if (_instance) {
                try { _instance.destroy(); } catch (e) {}
                _instance = null;
            }

            return loadSdk().then(function (XmovAvatar) {
                return new Promise(function (resolve, reject) {
                    var resolved = false;
                    var progressComplete = false;
                    var timer = null;

                    var initOpts = {
                        containerId: containerId,
                        appId: appId,
                        appSecret: appSecret,
                        gatewayServer: opts.gatewayServer || cfg.gatewayServer || DEFAULT_GATEWAY,
                        headers: opts.headers || cfg.headers || {},
                        hardwareAcceleration: opts.hardwareAcceleration || cfg.hardwareAcceleration || 'default',
                        enableLogger: opts.enableLogger === true,
                        onStateChange: function (state) {
                            if (typeof opts.onStateChange === 'function') opts.onStateChange(state);
                        },
                        onStatusChange: function (status) {
                            if (typeof opts.onStatusChange === 'function') opts.onStatusChange(status);
                        },
                        onStateRenderChange: function (state, duration) {
                            if (typeof opts.onStateRenderChange === 'function') opts.onStateRenderChange(state, duration);
                        },
                        onStartSessionWarning: function (message) {
                            if (typeof opts.onStartSessionWarning === 'function') opts.onStartSessionWarning(message);
                        },
                        onMessage: function (message) {
                            if (typeof opts.onMessage === 'function') opts.onMessage(message);
                        },
                        onNetworkInfo: function (info) {
                            if (typeof opts.onNetworkInfo === 'function') opts.onNetworkInfo(info);
                        },
                        onVoiceStateChange: function (status) {
                            if (typeof opts.onVoiceStateChange === 'function') opts.onVoiceStateChange(status);
                        },
                        onWidgetEvent: function (data) {
                            if (typeof opts.onWidgetEvent === 'function') opts.onWidgetEvent(data);
                        },
                        proxyWidget: opts.proxyWidget || {},
                    };

                    var inst;
                    try {
                        inst = new XmovAvatar(initOpts);
                        _instance = inst;

                        // init() / startSession() 时传入 onDownloadProgress
                        var initArgs = {};
                        if (typeof opts.onDownloadProgress === 'function') {
                            initArgs.onDownloadProgress = function (progress) {
                                opts.onDownloadProgress(progress);
                                // progress 达到 100 视为资源加载完成 → 就绪
                                if (progress >= 100 && !progressComplete) {
                                    progressComplete = true;
                                    if (!resolved) {
                                        resolved = true;
                                        if (timer) { clearTimeout(timer); timer = null; }
                                        resolve(inst);
                                    }
                                }
                            };
                        }

                        if (typeof inst.init === 'function') {
                            inst.init(initArgs);
                        } else if (typeof inst.startSession === 'function') {
                            inst.startSession(initArgs);
                        }

                        // 兜底超时：progress 未达 100 时，超时也 resolve（避免卡死）
                        timer = setTimeout(function () {
                            if (!resolved) {
                                resolved = true;
                                ttx.console.warn('[ttx.xingyun] 初始化就绪超时，强制 resolve');
                                resolve(inst);
                            }
                        }, READY_TIMEOUT);
                    } catch (e) {
                        if (timer) { clearTimeout(timer); timer = null; }
                        reject(e);
                    }
                });
            });
        },

        /**
         * 驱动数字人说话（非流式调用）
         * @param {string} text - 文本内容（可包含 SSML 标记）
         * @param {boolean} [isStart=true] - 是否是流式首句
         * @param {boolean} [isEnd=true] - 是否是流式末句
         */
        speak: function (text, isStart, isEnd) {
            if (!_instance) {
                ttx.console.warn('[ttx.xingyun] 数字人未初始化，无法 speak');
                return;
            }
            if (isStart === undefined) isStart = true;
            if (isEnd === undefined) isEnd = true;
            try {
                _instance.speak(text, isStart, isEnd);
            } catch (e) {
                ttx.console.error('[ttx.xingyun] speak 失败:', e);
            }
        },

        /** 待机 */
        idle: function () {
            if (_instance) { try { _instance.idle(); } catch (e) {} }
        },

        /** 待机互动（可用于打断当前状态回到待机） */
        interactiveIdle: function () {
            if (_instance) { try { _instance.interactiveidle(); } catch (e) {} }
        },

        /** 倾听 */
        listen: function () {
            if (_instance) { try { _instance.listen(); } catch (e) {} }
        },

        /** 思考 */
        think: function () {
            if (_instance) { try { _instance.think(); } catch (e) {} }
        },

        /** 设置音量 0~1 */
        setVolume: function (volume) {
            if (_instance) { try { _instance.setVolume(volume); } catch (e) {} }
        },

        /** 切换隐身/在线 */
        switchInvisibleMode: function () {
            if (_instance) { try { _instance.switchInvisibleMode(); } catch (e) {} }
        },

        /** UI 层面显示/隐藏数字人 */
        changeAvatarVisible: function (visible) {
            if (_instance) { try { _instance.changeAvatarVisible(visible); } catch (e) {} }
        },

        /** 销毁当前数字人实例，断开连接并清理资源 */
        destroy: function () {
            if (!_instance) return;
            try { _instance.destroy(); } catch (e) {}
            _instance = null;
        },

        /** 获取当前数字人实例 */
        getInstance: function () {
            return _instance;
        }
    };
})();
